import iconsPng from '@/assets/img/png/icons';
import SvgIcon from '@/components/atoms/SvgIcon';
import svgIcons from '@/assets/svg/icons';
import colors from '@/theme/colors';
import { OPTION_OTHER } from '@/utils/demographicDataMappers';
import { Button, HStack, Image, Text, VStack } from '@chakra-ui/react';
import UserProfileBox from './UserProfileBox';
import UserProfileBoxInfoPiece from './UserProfileBoxInfoPiece';
import { useMemo } from 'react';
import { displayDateFromBeWithTimeZone } from '@/utils/date';

const ITEMS_GAP_PX = '24px';

interface UserDemographicsCardProps {
  dateOfBirth: string;
  sex: string;
  sexOther?: string;
  occupation: string;
  occupationOther?: string;
  ethnicity: string;
  ethnicityOther?: string;
  relationshipStatus: string;
  relationshipStatusOther?: string;
  education: string;
  educationOther?: string;
  onClickEdit: () => void;
  hideEditButton?: boolean;
  showSkippedState?: boolean;
}

const UserDemographicsBox = ({
  dateOfBirth,
  sex,
  sexOther,
  occupation,
  occupationOther,
  ethnicity,
  ethnicityOther,
  relationshipStatus,
  relationshipStatusOther,
  education,
  educationOther,
  onClickEdit,
  hideEditButton = false,
  showSkippedState = false,
}: UserDemographicsCardProps) => {
  const dateOfBirthDisplay = useMemo(() => displayDateFromBeWithTimeZone(dateOfBirth), [dateOfBirth]);

  if (showSkippedState) {
    return (
      <UserProfileBox style={{ height: '160px' }}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text variant="urbanistSemiBoldTitle">Demographics</Text>
          {!hideEditButton && (
            <Button
              variant="link"
              color="primary.500"
              _hover={{ opacity: 0.7, textDecoration: 'none' }}
              onClick={onClickEdit}
              fontSize="16px"
              leftIcon={
                <SvgIcon iconPath={svgIcons.plus} color={colors.primary[500]} size={15} boxHeight={16} boxWidth={16} />
              }
            >
              Add
            </Button>
          )}
        </HStack>
        <Text variant="urbanistSemiBoldRegular" color="text.mediumGray" marginTop="32px">
          Add a few details to help personalize your wholeness experience.
        </Text>
      </UserProfileBox>
    );
  }

  return (
    <UserProfileBox style={{ height: '360px' }}>
      <HStack justifyContent={'space-between'}>
        <Text variant={'urbanistSemiBoldTitle'} marginBottom={'10px'}>
          Demographics
        </Text>
        {!hideEditButton && (
          <Button
            variant="link"
            color="primary.500"
            _hover={{ opacity: 0.7, textDecoration: 'none' }}
            onClick={onClickEdit}
            fontSize="16px"
            leftIcon={
              <Image
                alt=""
                src={iconsPng.penPurple}
                width="12px"
                height="12px"
                objectFit="cover"
                style={{ marginRight: '4px', marginBottom: '1px' }}
              />
            }
          >
            Edit
          </Button>
        )}
      </HStack>
      <HStack gap={'50px'} marginTop={'20px'}>
        <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
          <UserProfileBoxInfoPiece title={'DATE OF BIRTH'} text={dateOfBirthDisplay || '/'} />
          <UserProfileBoxInfoPiece title={'SEX'} text={(sex === OPTION_OTHER ? sexOther : sex) || '/'} />
        </VStack>
        <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
          {occupation && (
            <UserProfileBoxInfoPiece
              title={'OCCUPATION'}
              text={(occupation === OPTION_OTHER ? occupationOther : occupation) || '/'}
            />
          )}
          <UserProfileBoxInfoPiece
            title={'ETHNICITY'}
            text={(ethnicity === OPTION_OTHER ? ethnicityOther : ethnicity) || '/'}
          />
          <UserProfileBoxInfoPiece
            title={'RELATIONSHIP STATUS'}
            text={(relationshipStatus === OPTION_OTHER ? relationshipStatusOther : relationshipStatus) || '/'}
          />
          <UserProfileBoxInfoPiece
            title={'HIGHEST LEVEL OF EDUCATION'}
            text={(education === OPTION_OTHER ? educationOther : education) || '/'}
          />
        </VStack>
      </HStack>
    </UserProfileBox>
  );
};

export default UserDemographicsBox;
